import { useEffect, useState } from "react";
import { startRegistration, startAuthentication } from "@simplewebauthn/browser";
import { api, ApiError, type DevClinician } from "./api.ts";
import { Avatar, Banner, Btn, Icon } from "./ui.tsx";

const Mark = () => <h1 className="mark">page<span>-com</span></h1>;

/**
 * Sign in.
 *
 * No username, no password, no email. The passkey is discoverable, so the phone
 * offers the right identity itself. There is nothing on this screen to type,
 * phish or forget.
 */
export function SignIn({ onDone, teams, teamsError, patientData }: { onDone: () => void; teams?: boolean; teamsError?: string | null; patientData?: boolean }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devLogin, setDevLogin] = useState(false);
  const [people, setPeople] = useState<DevClinician[]>([]);

  useEffect(() => {
    api.authConfig()
      .then((c) => {
        setDevLogin(c.devLogin);
        if (c.devLogin) return api.devClinicians().then((r) => setPeople(r.clinicians));
      })
      .catch(() => setDevLogin(false));
  }, []);

  const signInAs = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.devLogin(id);
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not sign in.");
    } finally {
      setBusy(false);
    }
  };

  const signIn = async () => {
    setBusy(true);
    setError(null);
    try {
      const { options } = await api.loginOptions();
      const response = await startAuthentication({ optionsJSON: options as never });
      await api.loginVerify(response);
      onDone();
    } catch (e) {
      if (e instanceof Error && e.name === "NotAllowedError") setError("Sign-in was cancelled.");
      else setError(e instanceof ApiError ? e.message : "Could not sign in on this device.");
    } finally {
      setBusy(false);
    }
  };

  if (teams) {
    return (
      <main className="auth">
        <Mark />
        <p className="tag">Signing you in with your Microsoft account…</p>
        {teamsError && <Banner kind="err">{teamsError}</Banner>}
        {teamsError && <Btn full lg onClick={() => window.location.reload()}>Try again</Btn>}
      </main>
    );
  }

  return (
    <main className="auth">
      <Mark />
      <p className="tag">Clinical paging with acknowledgement, escalation and a discussion on every page.</p>
      {patientData && <div className="demo-strip" role="note">DEMO · made-up patients only · never enter real patient details</div>}
      {error && <Banner kind="err">{error}</Banner>}
      <Btn full lg onClick={() => void signIn()} disabled={busy}>
        <Icon name="user" size={16} sw={2.2} />{busy ? "Waiting for your device…" : "Sign in with Face ID or Touch ID"}
      </Btn>
      <p className="fine">No password. Your passkey lives on this device. New device? Ask an admin for an enrolment link.</p>

      {devLogin && (
        <section aria-labelledby="devhead" style={{ marginTop: 34 }}>
          <div className="sec" id="devhead">Sign in as</div>
          {people.map((c) => (
            <button key={c.id} className="popt" onClick={() => void signInAs(c.id)} disabled={busy}>
              <Avatar name={c.name} size={34} fontSize={14} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="nm">{c.name}</div>
                <div className="tt">{c.title} · {c.service}</div>
              </div>
            </button>
          ))}
        </section>
      )}
    </main>
  );
}

/** Turns a single-use enrolment link into a passkey on this device. */
export function Enrol({ token, onDone }: { token: string; onDone: () => void }) {
  const [who, setWho] = useState<{ name: string; title: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [options, setOptions] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    api.enrolOptions(token)
      .then((r) => { setWho(r.clinician); setOptions(r.options); })
      .catch((e) => setError(e instanceof ApiError ? e.message : "Could not open this link."));
  }, [token]);

  const enrol = async () => {
    if (!options) return;
    setBusy(true);
    setError(null);
    try {
      const response = await startRegistration({ optionsJSON: options as never });
      await api.enrolVerify(token, response);
      onDone();
    } catch (e) {
      if (e instanceof Error && e.name === "NotAllowedError") setError("Enrolment was cancelled.");
      else setError(e instanceof ApiError ? e.message : "Could not enrol this device.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="auth">
      <Mark />
      <p className="tag">Set up this device.</p>
      {error && <Banner kind="err">{error}</Banner>}
      {who && (
        <>
          <p className="who">Enrolling <b>{who.name}</b> · {who.title}</p>
          <Btn full lg onClick={() => void enrol()} disabled={busy || !options}>
            {busy ? "Waiting for your device…" : "Create a passkey on this phone"}
          </Btn>
          <p className="fine">Then add page-com to your home screen and turn on notifications. On iPhone both steps are needed, in that order.</p>
        </>
      )}
    </main>
  );
}
